import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const LINKS = [
  { label: "Products", to: "/?section=products" },
  { label: "Why LENIS", to: "/?section=standard" },
  { label: "Compare protein", to: "/?section=compare" },
  { label: "About", to: "/?section=about" },
  { label: "Reviews", to: "/?section=reviews" },
  { label: "FAQ", to: "/?section=faq" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const { count, setIsOpen } = useCart();
  const { user, openAuth } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuToggle = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = event => { if (event.key === "Escape") { setMobileOpen(false); menuToggle.current?.focus(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const onAccount = () => (user ? navigate("/account") : openAuth("login"));

  return (
    <header data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#080806]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-[70px] items-center justify-between">
          <Logo />
          <nav aria-label="Main navigation" className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <Link to={l.to} key={l.label} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setMobileOpen(false)} className="inline-flex min-h-[44px] items-center text-sm font-medium text-[#F7F6F0] hover:text-[#D6A921] transition-colors">
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <button onClick={onAccount} data-testid="nav-account-button"
              className="hidden sm:flex h-11 items-center gap-2 rounded-full border border-white/15 px-4 text-sm font-medium text-white hover:border-[#D6A921] hover:text-[#D6A921] transition-colors">
              <User size={16} />
              {user ? user.name.split(" ")[0] : "Sign in"}
            </button>

            <button data-testid="cart-drawer-trigger" aria-label="Open shopping cart" onClick={() => setIsOpen(true)}
              className="relative flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white hover:border-[#D6A921] hover:text-[#D6A921] transition-colors">
              <ShoppingBag size={19} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#D6A921] text-xs font-bold text-[#080806]">
                  {count}
                </span>
              )}
            </button>

            <button ref={menuToggle} className="lg:hidden flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
              onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} aria-controls="mobile-navigation">
              {mobileOpen ? <X size={19} /> : <Menu size={19} />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <nav aria-label="Mobile navigation" id="mobile-navigation" className="lg:hidden border-t border-white/10 bg-[#080806]/95 backdrop-blur-xl px-5 py-4">
          <Logo testId="mobile-brand-logo" />
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <Link to={l.to} key={l.label} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="py-3 text-left text-base font-semibold text-[#F7F6F0] border-b border-white/5">{l.label}</Link>
            ))}
            <button onClick={() => { setMobileOpen(false); onAccount(); }} className="py-3 text-left text-base font-semibold text-[#D6A921]">
              {user ? "My Account" : "Sign in"}
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}


